// Seeding list-page filter state FROM the querystring.
//
// Every list page (contracts, TPRM requests, reviews, admin intakes) seeds its
// filters out of the URL so a filtered view is shareable and Back restores it.
// The hole that opened up (doc-194): nothing checked the value against the
// page's own option list. /admin/intakes?status=bogus APPLIES the filter — the
// fetch carries it and the list comes back empty — while the <select> still
// reads "All statuses", because no <option value="bogus"> exists to select.
// Picking "All statuses" then fires no change event, so the control physically
// cannot clear the filter it is misreporting. Real users reach this through any
// shared or bookmarked link written before an enum value was renamed or retired.
//
// The companion half is the search box: writing the RAW value to the URL while
// fetching the TRIMMED one lets the two disagree, so ?q=%20%20 renders a
// filled-looking box, keeps ?q=++ in the URL, and filters nothing. Both the
// read and the write go through normalizeSearchValue here so they cannot drift
// apart again.
//
// SCOPE — only STATIC option lists may be validated this way: module-level
// constants that exist on the first render (intake type/status, request
// status/risk, review status/type/triage, contract status/category/entity).
// Facet-driven lists — contract type, counterparty, counterparty group, tag —
// start life as a single "All" entry and are filled by a later fetch, so
// validating them at mount would scrub every legitimate deep-linked value, and
// there is no later moment that is correct either (the facet list is not
// guaranteed to enumerate every value present in the data). Leave those alone.

// Option lists in this codebase are {value, label} objects; plain strings are
// accepted too so a bare enum array can be handed straight in.
function valueOf(option) {
  return option && typeof option === 'object' ? option.value : option;
}

// Resolve a URL-supplied value to the OPTION'S OWN spelling, matching case
// -insensitively, or '' when the page has no option for it (doc-201).
//
// Case-insensitively, because three backend list filters already lower/upper
// -case the value before they match — document-review-service type and
// aiClassification, request-crud risk — so ?triage=green, ?type=NDA and
// ?risk=High used to filter correctly. Rejecting them outright would take that
// working behaviour away from anyone who hand-typed or hand-edited a URL.
//
// Returning the option's spelling rather than the caller's is what keeps
// doc-194's invariant intact: the value handed back is always one a <select>
// can display, so ?triage=green seeds 'GREEN' and the select, the chip, the URL
// and the fetch all agree. Widening the MATCH without canonicalising the RESULT
// would just re-open doc-194 for every non-canonical spelling.
export function canonicalOption(value, options) {
  if (!value || typeof value !== 'string') return '';
  const folded = value.toLowerCase();
  const match = (options || []).find((option) => {
    const optionValue = valueOf(option);
    return typeof optionValue === 'string' && optionValue.toLowerCase() === folded;
  });
  // The "All" sentinel is spelled '' in every list here, so a value that folds
  // onto it is no filter at all — same answer as an absent param.
  return match ? valueOf(match) : '';
}

// Is `value` already exactly one of the page's own selectable option values?
// Exact, unlike canonicalOption: this answers membership, so a caller cannot
// use a true here to justify STORING a non-canonical spelling in state. The
// "All" sentinel is spelled '', so an absent param and the All entry agree.
export function isKnownOption(value, options) {
  if (!value) return false;
  return canonicalOption(value, options) === value;
}

// Seed one select-backed filter from the URL: canonicalise what the page can
// display, drop what it cannot. Dropping it is also what SCRUBS the key: each
// list's URL-sync effect deletes the key whenever its state is empty, so the
// bogus param is gone from the querystring on the first paint. A value that
// only differed in case is instead REWRITTEN to the canonical spelling by that
// same effect.
export function readOptionParam(searchParams, key, options) {
  return canonicalOption(searchParams.get(key), options);
}

// The single definition of "what this search term actually is" — used when
// seeding from the URL, when writing back to the URL, and when building the
// fetch, so the URL, the box and the request can never disagree.
export function normalizeSearchValue(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function readSearchParam(searchParams, key = 'q') {
  return normalizeSearchValue(searchParams.get(key));
}
